import React, { useEffect } from 'react'

interface LegalPageProps {
  type: 'terms' | 'privacy'
}

function PrivacyContent() {
  return (
    <>
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Privacy Policy</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">Last Updated: November 28, 2025</p>

      <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
        This Privacy Policy describes how Outlier Labs Inc. ("Outlier Labs," "we," "us," or "our") collects, uses, shares, and protects information in connection with your use of the Insight Atlas platform, websites, mobile applications, and related services (the "Service").
      </p>

      <Section title="1. Information We Collect">
        <ul className="list-disc pl-5 text-gray-600 dark:text-gray-400 space-y-2">
          <li><strong>Account Information:</strong> Name, email address, password, organization, role, and profile information you provide.</li>
          <li><strong>Billing Information:</strong> Payment card details (processed by our payment processor), billing address, and subscription details.</li>
          <li><strong>User Content:</strong> Research queries, prompts, documents, reports, notes, uploaded files, and other content you submit.</li>
          <li><strong>Usage Data:</strong> Log data about how you access and use the Service.</li>
          <li><strong>Device and Technical Data:</strong> IP address, browser type, device type, operating system, and identifiers.</li>
        </ul>
      </Section>

      <Section title="2. How We Use Your Information">
        <p className="text-gray-600 dark:text-gray-400">
          We use information to provide and operate the Service, improve and develop features, communicate with you, and maintain safety, security, and compliance with legal obligations.
        </p>
      </Section>

      <Section title="3. How We Share Information">
        <p className="text-gray-600 dark:text-gray-400">
          We may share information with service providers who perform services on our behalf (cloud hosting, payment processing, customer support, analytics). When you use AI-related features, your prompts and queries may be sent to third-party AI model providers. We do not sell your personal information to data brokers.
        </p>
      </Section>

      <Section title="4. Data Retention">
        <p className="text-gray-600 dark:text-gray-400">
          We retain personal information for as long as necessary to provide the Service, comply with legal obligations, resolve disputes, and enforce agreements. You may be able to delete certain User Content and account information via the Service.
        </p>
      </Section>

      <Section title="5. Security">
        <p className="text-gray-600 dark:text-gray-400">
          We use reasonable administrative, technical, and physical safeguards designed to protect your personal information against unauthorized access, loss, misuse, or alteration. However, no system is perfectly secure.
        </p>
      </Section>

      <Section title="6. Your Rights and Choices">
        <p className="text-gray-600 dark:text-gray-400">
          You may have the right to access the personal information we hold about you, correct inaccurate or incomplete data, and delete personal information. You can opt out of marketing emails by using the "unsubscribe" link.
        </p>
      </Section>

      <Section title="7. California Residents (CCPA/CPRA)">
        <p className="text-gray-600 dark:text-gray-400">
          California residents may have additional rights under the California Consumer Privacy Act, including the right to know what categories of personal information we collect, the right to access specific pieces of personal information, and the right to request deletion. We do not sell personal information.
        </p>
      </Section>

      <Section title="8. Children's Privacy">
        <p className="text-gray-600 dark:text-gray-400">
          The Service is not directed to children under 13, and we do not knowingly collect personal information from children under 13 without appropriate parental consent.
        </p>
      </Section>

      <Section title="9. Contact Us">
        <p className="text-gray-600 dark:text-gray-400">
          For questions, contact: <a href="mailto:admin@outlierlabs.io" className="text-gray-900 dark:text-white underline underline-offset-2 hover:text-black dark:hover:text-gray-200 transition-colors">admin@outlierlabs.io</a>
          <br />Outlier Labs Inc., California, USA
        </p>
      </Section>
    </>
  )
}

function TermsContent() {
  return (
    <>
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Terms of Service</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">Last Updated: November 28, 2025</p>

      <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
        These Terms of Service ("Terms") form a binding agreement between you ("you" or "User") and Outlier Labs Inc. ("Outlier Labs," "we," "us," or "our") governing your access to and use of the Insight Atlas platform, websites, mobile applications, and related services (collectively, the "Service").
      </p>
      <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
        By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.
      </p>

      <Section title="1. Eligibility">
        <p className="text-gray-600 dark:text-gray-400">
          You must be at least 18 years old and able to enter a binding contract to use the Service. If you are using the Service on behalf of a company or other legal entity, you represent and warrant that you have authority to bind that entity to these Terms.
        </p>
      </Section>

      <Section title="2. Account Registration and Security">
        <p className="text-gray-600 dark:text-gray-400">
          To use certain features of the Service, you must create an account and provide accurate, complete, and current information. You are responsible for maintaining the confidentiality of your account credentials, all activities that occur under your account, and promptly notifying us of any unauthorized access or suspected breach.
        </p>
      </Section>

      <Section title="3. Description of the Service">
        <p className="text-gray-600 dark:text-gray-400">
          Insight Atlas provides tools for researching topics, generating and analyzing content using AI models, and organizing research outputs (including reports, notes, insights, and related content). The Service may incorporate or interact with third-party AI models, APIs, and services.
        </p>
      </Section>

      <Section title="4. Subscription Plans, Fees, and Billing">
        <p className="text-gray-600 dark:text-gray-400">
          Certain features of the Service are provided on a subscription or usage-based basis. By subscribing to Paid Services, you agree to pay all applicable fees, taxes, and charges. Fees are typically charged via a third-party payment processor (e.g., Stripe). Unless cancelled, your subscription will automatically renew at the end of each billing period.
        </p>
      </Section>

      <Section title="5. User Content and Ownership">
        <p className="text-gray-600 dark:text-gray-400">
          As between you and us, you retain all rights, title, and interest in and to the User Content you submit, generate, or upload through the Service. You grant Outlier Labs a worldwide, non-exclusive, royalty-free license to host, store, process, use, display, and create derivative works of your User Content solely for the purpose of operating the Service.
        </p>
      </Section>

      <Section title="6. Prohibited Uses">
        <p className="text-gray-600 dark:text-gray-400">
          You agree not to use the Service to: violate any applicable law; infringe the intellectual property, privacy, or other rights of others; upload illegal, defamatory, harassing, or otherwise objectionable content; generate content intended to mislead, defraud, or cause harm; or attempt to gain unauthorized access to the Service.
        </p>
      </Section>

      <Section title="7. AI-Generated Content and No Professional Advice">
        <p className="text-gray-600 dark:text-gray-400">
          The Service may generate outputs, suggestions, or summaries using AI models. These outputs may be inaccurate, incomplete, or outdated. You are solely responsible for evaluating and verifying any outputs before relying on them. The Service does not constitute legal, financial, medical, tax, or other professional advice.
        </p>
      </Section>

      <Section title="8. Disclaimers">
        <p className="text-gray-600 dark:text-gray-400 uppercase text-sm tracking-wide">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY. OUTLIER LABS DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
        </p>
      </Section>

      <Section title="9. Limitation of Liability">
        <p className="text-gray-600 dark:text-gray-400 uppercase text-sm tracking-wide">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUTLIER LABS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES. OUTLIER LABS' TOTAL AGGREGATE LIABILITY WILL NOT EXCEED THE GREATER OF THE AMOUNT YOU PAID FOR THE SERVICE DURING THE THREE MONTHS PRIOR TO THE EVENT GIVING RISE TO LIABILITY OR US $100.
        </p>
      </Section>

      <Section title="10. Governing Law">
        <p className="text-gray-600 dark:text-gray-400">
          These Terms are governed by the laws of the State of California, without regard to conflict of law principles, and by applicable federal law of the United States. Any dispute arising out of or relating to these Terms or the Service will be resolved by binding arbitration.
        </p>
      </Section>

      <div className="mt-8 text-gray-600 dark:text-gray-400 text-sm">
        For questions, contact: <a href="mailto:admin@outlierlabs.io" className="text-gray-900 dark:text-white underline underline-offset-2 hover:text-black dark:hover:text-gray-200 transition-colors">admin@outlierlabs.io</a>
        <br />Outlier Labs Inc., California, USA
      </div>
    </>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-8">
      <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">{title}</h2>
      {children}
    </div>
  )
}

export default function LegalPage({ type }: LegalPageProps) {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [type])

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white">
      <div className="max-w-2xl mx-auto px-6 py-16 sm:py-24">
        <a
          href="/"
          className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors mb-10"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Insight Atlas
        </a>

        <div className="prose prose-sm dark:prose-invert max-w-none">
          {type === 'privacy' ? <PrivacyContent /> : <TermsContent />}
        </div>

        <div className="mt-16 pt-8 border-t border-gray-200 dark:border-gray-800">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-400 dark:text-gray-500">
            <div className="flex items-center gap-3">
              <img src="/logo192.png" alt="Insight Atlas" className="w-6 h-6 rounded-md" />
              <span>Insight Atlas</span>
            </div>
            <div className="flex items-center gap-4">
              {type !== 'privacy' && (
                <a href="/privacy.html" className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                  Privacy Policy
                </a>
              )}
              {type !== 'terms' && (
                <a href="/terms.html" className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                  Terms of Service
                </a>
              )}
              <a href="mailto:admin@outlierlabs.io" className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                Contact
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
